"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, MapPin, ChartBar as BarChart3, Target, ArrowRight, CircleCheck as CheckCircle2, Building2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const metricsData = [{
  id: "m1",
  title: "Local Search Growth",
  value: "+34% YoY",
  description: "Surge in Peterborough commercial & B2B search queries over the past 12 months.",
  statLabel: "Demand Spike"
}, {
  id: "m2",
  title: "Mobile Search Share",
  value: "68%",
  description: "Local queries in Peterborough originating from mobile devices seeking immediate services.",
  statLabel: "Mobile Intent"
}, {
  id: "m3",
  title: "Map Pack Click-Through",
  value: "72%",
  description: "Organic traffic captured by the top 3 Google Business Profile listings in Cambridgeshire.",
  statLabel: "Local Dominance"
}, {
  id: "m4",
  title: "Competitor Gap",
  value: "High Potential",
  description: "Unclaimed keyword clusters in Peterborough professional & industrial sectors.",
  statLabel: "Opportunity Window"
}];
const keywordData = [{
  keyword: "SEO consultant Peterborough",
  volume: "320 / mo",
  difficulty: "Low (24/100)",
  intent: "High Commercial",
  opportunity: "Direct access positioning captures high-value clients avoiding agency markups."
}, {
  keyword: "B2B services Peterborough",
  volume: "880 / mo",
  difficulty: "Medium (38/100)",
  intent: "Commercial / B2B",
  opportunity: "Target regional decision-makers in manufacturing, logistics, and professional services."
}, {
  keyword: "Logistics SEO East Anglia",
  volume: "210 / mo",
  difficulty: "Low (18/100)",
  intent: "Commercial Niche",
  opportunity: "Capitalize on Peterborough's position as a premier UK distribution hub."
}, {
  keyword: "Local SEO services Cambridgeshire",
  volume: "590 / mo",
  difficulty: "Medium (42/100)",
  intent: "High Intent",
  opportunity: "Cross-regional visibility reaching Cambridge, Huntingdon, and Peterborough buyers."
}];
const opportunitiesData = [{
  number: "01",
  title: "Technical & Local Schema Architecture",
  detail: "Eliminate crawl bottlenecks, resolve site speed issues, and implement structured Geo-Schema markups specific to your Peterborough business entity."
}, {
  number: "02",
  title: "Intent-Driven Regional Content",
  detail: "Replace generic brochure copy with targeted landing pages designed to answer exact search queries from local decision-makers."
}, {
  number: "03",
  title: "Google Business Profile & Citation Alignment",
  detail: "Maximize Google Map Pack placement through geo-targeted updates, accurate citation networks, and localized review building."
}, {
  number: "04",
  title: "Regional Digital Footprint & Entity Signals",
  detail: "Establish local search authority through high-quality East Anglia industry media mentions, local sponsorships, and regional backlinks."
}];
const neighboringCities = [{
  name: "Milton Keynes",
  distance: "48 miles",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  desc: "Tech hub & corporate search strategy in Buckinghamshire"
}, {
  name: "Northampton",
  distance: "42 miles",
  path: "/LocalSeoConsultantNorthamptonPage",
  desc: "Logistics & industrial B2B local SEO services"
}, {
  name: "Derby",
  distance: "65 miles",
  path: "/LocalSeoConsultantDerbyPage",
  desc: "Engineering & manufacturing organic search growth"
}, {
  name: "Wakefield",
  distance: "110 miles",
  path: "/LocalSeoConsultantWakefieldPage",
  desc: "Regional B2B growth and targeted local optimization"
}, {
  name: "Stoke-on-Trent",
  distance: "105 miles",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  desc: "Commercial service provider SEO & digital strategy"
}, {
  name: "All Local SEO Services",
  distance: "UK Wide",
  path: "/LocalSEOPage",
  desc: "Full bespoke UK local search strategy breakdown"
}];
export default function PeterboroughMarket() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="peterboroughmarketanalysis" data-nav="light" className="bg-background text-foreground py-20 md:py-28 overflow-hidden transition-colors">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-sm bg-secondary text-secondary-foreground border border-accent/20 rounded-full font-medium">
            <MapPin className="w-3.5 h-3.5 text-primary mr-1" /> Peterborough & Cambridgeshire Search Intelligence
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Peterborough SEO Market Analysis & Growth Opportunities
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            I evaluate Peterborough&apos;s digital market to uncover high-intent search volume, competitor gaps, and actionable SEO strategies. Work directly with me to capture organic search share across Cambridgeshire and the East Midlands.
          </p>
        </motion.div>

        {/* Market Opportunity Metrics */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {metricsData.map((metric, i) => <div data-index={i} key={metric.id}>
              <motion.div variants={itemVariants} className="h-full">
                <Card className="h-full bg-card text-card-foreground border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] hover:border-primary/30">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {metric.statLabel}
                      </span>
                      <TrendingUp className="w-4 h-4 text-primary" />
                    </div>
                    <CardTitle className="text-3xl lg:text-4xl font-bold font-default text-primary pt-1">
                      {metric.value}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    <h3 className="font-semibold text-foreground text-sm font-default">
                      {metric.title}
                    </h3>
                    <CardDescription className="text-xs text-muted-foreground leading-relaxed">
                      {metric.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        {/* Keyword Intelligence & Local Competitor Gap */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 items-stretch">
          
          {/* Keyword Insights Table Card */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 flex flex-col justify-between">
            <Card className="h-full bg-card text-card-foreground border-border shadow-sm flex flex-col justify-between">
              <CardHeader className="border-b border-border/60 pb-5">
                <div className="flex items-center gap-2">
                  <Search className="w-5 h-5 text-primary" />
                  <CardTitle className="text-xl font-semibold font-default text-foreground">
                    High-Value Peterborough Keyword Analysis
                  </CardTitle>
                </div>
                <CardDescription className="text-muted-foreground text-sm">
                  Representative search query breakdown showing current local demand and organic conversion potential.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 divide-y divide-border/60">
                {keywordData.map((kw, i) => <div data-index={i} key={kw.keyword} className="p-5 hover:bg-muted/40 transition-colors">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <span className="font-medium text-foreground text-sm md:text-base font-default flex items-center gap-2">
                        <Target className="w-4 h-4 text-primary shrink-0" />
                        {kw.keyword}
                      </span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs font-mono border-border">
                          {kw.volume}
                        </Badge>
                        <Badge variant="secondary" className="text-xs bg-secondary text-secondary-foreground">
                          {kw.intent}
                        </Badge>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      <strong className="text-foreground font-medium">Opportunity:</strong> {kw.opportunity}
                    </p>
                  </div>)}
              </CardContent>
            </Card>
          </motion.div>

          {/* Peterborough Search Reality Check */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5 flex flex-col justify-between">
            <Card className="h-full bg-primary text-primary-foreground border-transparent shadow-md p-6 lg:p-8 flex flex-col justify-between">
              <div className="space-y-6">
                <div className="flex items-center gap-2 text-accent">
                  <BarChart3 className="w-5 h-5" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-primary-foreground/90">
                    Competitor Organic Gap
                  </span>
                </div>

                <h3 className="text-2xl font-semibold font-default text-primary-foreground">
                  Why Peterborough Businesses Are Losing Organic Search Leads
                </h3>

                <p className="text-primary-foreground/90 text-sm leading-relaxed">
                  Many Peterborough businesses rely on legacy agency retainer models that deliver superficial monthly reports without addressing core technical flaws, poor local entity mapping, or missing AI Search (GEO) visibility.
                </p>

                <ul className="space-y-3 pt-2 text-sm text-primary-foreground/90">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>Unoptimized Google Business Profiles missing geo-tagged updates</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>Slow page load speeds and failing Core Web Vitals metrics</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>Generic, unlocalized content failing commercial search intent</span>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                    <span>No structured local schema markup or entity authority building</span>
                  </li>
                </ul>
              </div>

              <div className="pt-8">
                <Separator className="bg-primary-foreground/20 mb-6" />
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-primary-foreground/80">Direct Consultant Access</p>
                    <p className="text-sm font-semibold text-primary-foreground">Work Directly With Me</p>
                  </div>
                  <Button asChild variant="secondary" size="sm" className="bg-background text-foreground hover:bg-background/90">
                    <Link to="/ContactPage">
                      Book Audit <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        {/* 4-Step Strategic Methodology for Peterborough */}
        <div className="mb-20">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h3 className="text-2xl md:text-3xl font-semibold font-default text-foreground mb-3">
              My 4-Step Peterborough SEO Action Plan
            </h3>
            <p className="text-muted-foreground text-sm md:text-base">
              A methodical framework focused on clear deliverables, technical precision, and sustainable revenue growth.
            </p>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {opportunitiesData.map((op, i) => <div data-index={i} key={op.number}>
                <motion.div variants={itemVariants} className="h-full">
                  <Card className="h-full bg-card text-card-foreground border-border shadow-sm p-6 relative flex flex-col justify-between">
                    <div>
                      <span className="text-3xl font-extrabold font-mono text-primary/30 block mb-3">
                        {op.number}
                      </span>
                      <h4 className="font-semibold text-foreground text-lg mb-2 font-default">
                        {op.title}
                      </h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {op.detail}
                      </p>
                    </div>
                  </Card>
                </motion.div>
              </div>)}
          </motion.div>
        </div>

        {/* Neighboring Regional Hubs Navigation */}
        <div className="bg-muted/40 rounded-2xl p-6 md:p-10 border border-border">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 text-primary text-sm font-medium mb-1">
                <Building2 className="w-4 h-4" />
                <span>Regional Coverage & Cross-City SEO</span>
              </div>
              <h3 className="text-xl md:text-2xl font-semibold font-default text-foreground">
                SEO Services Across Nearby Regions
              </h3>
            </div>
            <p className="text-xs md:text-sm text-muted-foreground max-w-md">
              Expanding your search footprint beyond Peterborough? I optimize organic performance across key commercial hubs in the UK.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <div data-index={i} key={city.name}>
                <Card className="bg-card hover:bg-card/80 border-border shadow-2xs hover:shadow-xs transition-[background-color,box-shadow,border-color] hover:border-primary/40">
                  <CardContent className="p-4 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-semibold text-foreground text-base font-default">
                          {city.name}
                        </span>
                        <Badge variant="outline" className="text-[11.5px] font-mono">
                          {city.distance}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-3">
                        {city.desc}
                      </p>
                    </div>
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between text-xs text-primary hover:text-primary hover:bg-muted p-0 h-auto pt-2 font-medium">
                      <Link to={city.path}>
                        <span>Explore {city.name} Strategy</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Final Section CTA */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-16 text-center bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm space-y-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium">
            <Sparkles className="w-3.5 h-3.5 text-accent" />
            <span>Honest Measurement & Tailored SEO Strategy</span>
          </div>
          
          <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground max-w-2xl mx-auto tracking-tight">
            Ready to Accelerate Organic Revenue in Peterborough?
          </h3>
          
          <p className="text-muted-foreground text-sm md:text-base max-w-xl mx-auto leading-relaxed">
            Book a free, zero-obligation SEO consultation directly with me, Yassine Essaadi. We will review your search benchmarks, target keywords, and technical roadmap.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/SeoAuditsPage">
                Explore Full SEO Audits
              </Link>
            </Button>
          </div>
        </motion.div>

      </div>
    </section>;
}